/**
 * Template Gallery Cache - IndexedDB Storage
 * 
 * Caches rendered template HTML strings in IndexedDB to prevent:
 * - Blank white cards on page refresh
 * - Redundant network fetches on revisit
 * - Long sequential rendering on every page load
 * 
 * Cache is auto-invalidated when CACHE_VERSION changes (on redeployment).
 * Cache TTL: 1 day
 */

const DB_NAME = 'resumeglobe-template-cache';
const DB_VERSION = 1;
const STORE_NAME = 'rendered-templates';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 1 day

// Bump this on every deployment to force cache refresh for all users
const CACHE_VERSION = '2026-06-27-pure-css-cl-templates';
const CACHE_VERSION_KEY = 'rg-template-cache-version';

let dbInstance = null;
let cacheValidated = false;

/**
 * Check if cache version matches current deployment.
 * If not, clear the entire IndexedDB cache and update the version key.
 */
const validateCacheVersion = async () => {
  if (cacheValidated) return;
  cacheValidated = true;
  
  try {
    const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    if (storedVersion !== CACHE_VERSION) {
      console.log(`🔄 Template cache version changed (${storedVersion} → ${CACHE_VERSION}), clearing stale cache...`);
      // Clear IndexedDB
      const db = await getDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      await new Promise((resolve) => { tx.oncomplete = resolve; });
      // Update version
      localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
      console.log('✅ Template cache cleared and version updated');
    }
  } catch (err) {
    console.warn('Cache version check failed:', err.message);
    localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
  }
};

/**
 * Open/get the IndexedDB instance
 */
const getDB = () => {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
      }
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onerror = () => {
      console.warn('IndexedDB unavailable for template cache');
      reject(request.error);
    };
  });
};

/**
 * Save rendered template HTML to cache
 * @param {string} templateId - Unique template identifier
 * @param {string} renderedHTML - The rendered HTML string
 */
export const cacheRenderedTemplate = async (templateId, renderedHTML) => {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    store.put({
      id: templateId,
      html: renderedHTML,
      cachedAt: Date.now()
    });

    return new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    // Silently fail - cache is optional
    console.warn('Failed to cache template:', templateId, err.message);
  }
};

/**
 * Save multiple rendered templates to cache at once
 * @param {Object} renderedMap - { templateId: renderedHTML }
 */
export const cacheRenderedTemplates = async (renderedMap) => {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const now = Date.now();

    for (const [id, html] of Object.entries(renderedMap)) {
      if (html) {
        store.put({ id, html, cachedAt: now });
      }
    }

    return new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn('Failed to batch cache templates:', err.message);
  }
};

/**
 * Get a single cached template
 * @param {string} templateId
 * @returns {Promise<string|null>} Rendered HTML or null if not cached/expired
 */
export const getCachedTemplate = async (templateId) => {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);

    return new Promise((resolve) => {
      const request = store.get(templateId);
      request.onsuccess = () => {
        const entry = request.result;
        if (entry && (Date.now() - entry.cachedAt) < CACHE_TTL) {
          resolve(entry.html);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    });
  } catch (err) {
    return null;
  }
};

/**
 * Get multiple cached templates at once
 * @param {string[]} templateIds
 * @returns {Promise<Object>} { templateId: renderedHTML } for all cached entries
 */
export const getCachedTemplates = async (templateIds) => {
  const result = {};
  try {
    // Auto-clear stale cache if deployment version changed
    await validateCacheVersion();
    
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const now = Date.now();

    const promises = templateIds.map((id) => {
      return new Promise((resolve) => {
        const request = store.get(id);
        request.onsuccess = () => {
          const entry = request.result;
          if (entry && (now - entry.cachedAt) < CACHE_TTL) {
            result[id] = entry.html;
          }
          resolve();
        };
        request.onerror = () => resolve();
      });
    });

    await Promise.all(promises);
  } catch (err) {
    console.warn('Failed to load cached templates:', err.message);
  }
  return result;
};

/**
 * Clear all cached templates (useful for cache invalidation)
 */
export const clearTemplateCache = async () => {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    return new Promise((resolve) => {
      tx.oncomplete = resolve;
    });
  } catch (err) {
    console.warn('Failed to clear template cache:', err.message);
  }
};

/**
 * Remove expired entries from cache
 */
export const pruneExpiredCache = async () => {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const now = Date.now();

    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if ((now - cursor.value.cachedAt) >= CACHE_TTL) {
          cursor.delete();
        }
        cursor.continue();
      }
    };
  } catch (err) {
    // Non-critical
  }
};
